import PropTypes from 'prop-types'
import { useCallback, useState } from 'react'
import { useSelector } from 'react-redux'
import { useFlag } from '@calendly/feature-check-react'

import { pendingPhoneVerificationSelector, unverifiedPhoneNumberSelector, uuidSelector } from 'selectors/invitee'
import { useVerifySmsOtpMutation, useResendSmsOtpMutation, eventTypeSelector } from 'root-api'
import { useI18n } from 'locales'
import { eventService } from 'services/analytics/event_service'

import { PhoneVerificationModal } from './phone_verification_modal'

const SMS_OTP_ERROR_KEYS = {
  verification_incorrect: 'booking.phone_verification.error.verification_incorrect',
  verification_expired: 'booking.phone_verification.error.verification_expired',
  verification_rate_limited: 'booking.phone_verification.error.verification_rate_limited',
}

/**
 * @param {Function} onComplete - Optional additional callback after verification is complete
 * @returns {React.ReactNode} - Renders the Phone verification modal behind a feature flag and checks for pending phone verification
 */
export const PhoneVerificationFeature = ({ onComplete }) => {
  const { i18n } = useI18n()
  const phoneVerificationEnabled = useFlag('trust_and_safety_booking_sms_verification', false)
  const pendingPhoneVerification = useSelector(pendingPhoneVerificationSelector)
  const unverifiedPhoneNumber = useSelector(unverifiedPhoneNumberSelector)
  const inviteeUuid = useSelector(uuidSelector)
  const [isOpen, setIsOpen] = useState(true)
  const [verifyError, setVerifyError] = useState(null)

  const eventType = useSelector(eventTypeSelector)
  const { id: eventTypeId, uuid: eventTypeUuid, owning_user: owningUser, organization } = eventType ?? {}

  const [verifySmsOtp, { isLoading: isVerifying }] = useVerifySmsOtpMutation()
  const [resendSmsOtp, { isLoading: isResending }] = useResendSmsOtpMutation()

  const handleVerify = useCallback(async (code) => {
    setVerifyError(null)
    const result = await verifySmsOtp({ inviteeUuid, verificationCode: code })
    if (result.error) {
      const errorCode = result.error?.payload?.errors?.verification || result.error?.payload?.errors?.e164_phone_number
      const i18nKey = SMS_OTP_ERROR_KEYS[errorCode]
      setVerifyError(i18nKey ? i18n.t(i18nKey) : i18n.t('booking.phone_verification.error.verification_generic'))
      throw new Error(errorCode)
    }
  }, [verifySmsOtp, inviteeUuid, i18n])

  const handleResend = useCallback(async () => {
    setVerifyError(null)
    await resendSmsOtp({ inviteeUuid })
  }, [resendSmsOtp, inviteeUuid])

  const handleSkip = () => {
    eventService.dispatch({
      name: 'Invitee Booking Verification',
      version: 1,
      dependencies: {
        user_uuid: owningUser?.uuid,
        organization_uuid: organization?.uuid
      },
      entities: ['user', 'organization'],
      properties: {
        verification_method: 'sms_otp',
        action: 'skipped',
        event_type_id: eventTypeId,
        event_type_uuid: eventTypeUuid,
      }
    })
  }

  const handleComplete = useCallback(() => {
    setIsOpen(false)
    onComplete?.()
  }, [onComplete])

  if (!phoneVerificationEnabled || !pendingPhoneVerification) return null

  return (
    <PhoneVerificationModal
      isOpen={isOpen}
      phoneNumber={unverifiedPhoneNumber}
      onVerify={handleVerify}
      onResend={handleResend}
      onSkip={handleSkip}
      onComplete={handleComplete}
      isVerifying={isVerifying}
      isResending={isResending}
      error={verifyError}
    />
  )
}

PhoneVerificationFeature.propTypes = {
  onComplete: PropTypes.func,
}
